import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { BadgeCheck, Building2, Mail, X } from 'lucide-react'
import {
  PLATFORM_BRAND_NAME,
  PLATFORM_BUSINESS,
  PLATFORM_HAS_UDYAM,
} from '../constants/platformBusiness'

type PlatformBusinessRegistrationModalProps = {
  open: boolean
  onClose: () => void
}

type DetailRow = { label: string; value: string; mono?: boolean }

function buildDetailRows(): DetailRow[] {
  const rows: DetailRow[] = [
    { label: 'Name of enterprise', value: PLATFORM_BUSINESS.enterpriseName },
  ]
  if (PLATFORM_HAS_UDYAM) {
    rows.push(
      { label: 'Udyam registration number', value: PLATFORM_BUSINESS.udyamNumber, mono: true },
      { label: 'Date of registration', value: PLATFORM_BUSINESS.udyamRegistrationDate },
    )
  }
  if (PLATFORM_BUSINESS.businessRegNumber) {
    rows.push({ label: 'Business registration no.', value: PLATFORM_BUSINESS.businessRegNumber, mono: true })
  }
  if (PLATFORM_BUSINESS.gstNumber) {
    rows.push({ label: 'GSTIN', value: PLATFORM_BUSINESS.gstNumber, mono: true })
  }
  if (PLATFORM_BUSINESS.registeredAddress) {
    rows.push({ label: 'Registered address', value: PLATFORM_BUSINESS.registeredAddress })
  }
  return rows
}

export const PlatformBusinessRegistrationModal: React.FC<PlatformBusinessRegistrationModalProps> = ({
  open,
  onClose,
}) => {
  const detailRows = buildDetailRows()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="platform-business-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            aria-label="Close dialog"
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="platform-business-title"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-primary-600 via-primary-600 to-primary-700 text-white">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 rounded-lg p-1.5 text-white/80 hover:bg-white/15 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-start gap-3 pr-10">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                  <Building2 className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <p className="text-xs font-medium text-primary-100">Platform operator</p>
                  <h2 id="platform-business-title" className="text-xl font-bold mt-0.5 lowercase tracking-tight">
                    {PLATFORM_BRAND_NAME}
                  </h2>
                  <p className="text-sm text-primary-100/90 mt-1">Business registration &amp; compliance</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 max-h-[min(65vh,480px)] overflow-y-auto">
              {PLATFORM_HAS_UDYAM ? (
                <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 mb-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100">
                    <BadgeCheck className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">Udyam (MSME) registered</p>
                    <p className="text-xs text-emerald-700/90 mt-0.5">
                      Registered under the Government of India.
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                {detailRows.map((row) => (
                  <div key={row.label} className="px-4 py-3.5 bg-gray-50/50 even:bg-white">
                    <p className="text-xs font-medium text-gray-500">{row.label}</p>
                    <p className={`text-sm text-gray-900 mt-1 ${row.mono ? 'font-mono tracking-tight' : 'font-semibold'}`}>
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Mail className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500">Contact</p>
                  <a
                    href={`mailto:${PLATFORM_BUSINESS.contactEmail}`}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline truncate block"
                  >
                    {PLATFORM_BUSINESS.contactEmail}
                  </a>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}

export default PlatformBusinessRegistrationModal
